// src/pages/TherapistPage/CaseHistory/SignOffPanel.js
//
// Signing off an episode — the one action here that cannot be undone.
//
// So the screen is built to be read before it is used. Every requirement is
// listed with the reason it is not met, rather than a button that is simply
// grey: a therapist who can see what is missing can finish it, and one who
// cannot is left guessing. Advisories sit beside them — things that do not block
// signing but become permanent the moment the record is frozen.
//
// Afterwards the panel shows what was signed, by whom, and offers the frozen
// copy. There is no unlock, because there is no unlock: a correction opens a new
// episode.
import React, { useCallback, useEffect, useState } from 'react';
import { getSignOffState, getSnapshot, signOffCase, signOffError } from './signOffApi';

export default function SignOffPanel({ caseId, onChanged }) {
    const [state, setState] = useState(null);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [snapshot, setSnapshot] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const load = useCallback(() => {
        setError('');
        return getSignOffState(caseId)
            .then(({ data }) => { setState(data); setSnapshot(null); setConfirming(false); })
            .catch((err) => setError(signOffError(err, 'Could not load the sign-off state.')));
    }, [caseId]);

    useEffect(() => { load(); }, [load]);

    if (error && !state) return <div className="cf-note red">{error}</div>;
    if (!state) return <div className="cf-empty">Loading…</div>;

    const sign = async () => {
        setBusy(true); setError(''); setMessage('');
        try {
            const { data } = await signOffCase(caseId, note);
            setState(data);
            setConfirming(false);
            setMessage('Signed off. This episode is now read-only.');
            onChanged?.();
        } catch (err) {
            setError(signOffError(err, 'Could not sign off this episode.'));
        } finally { setBusy(false); }
    };

    const showSnapshot = async () => {
        setBusy(true); setError('');
        try {
            const { data } = await getSnapshot(caseId);
            setSnapshot(data);
        } catch (err) {
            setError(signOffError(err, 'Could not load the frozen record.'));
        } finally { setBusy(false); }
    };

    const when = (value) => (value
        ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : '');

    // ── already signed off ────────────────────────────────────────────────
    if (state.locked_at) {
        return (
            <div>
                {message && <div className="cf-note green">{message}</div>}
                {error && <div className="cf-note red">{error}</div>}

                <div className="cf-note green">
                    <b>Signed off on {when(state.locked_at)} by {state.locked_by_name}.</b>
                    {state.locked_by_name !== state.supervising_therapist_name && (
                        <div className="cf-hint">
                            The supervising therapist on this episode is {state.supervising_therapist_name}.
                        </div>
                    )}
                    <div className="cf-hint">
                        This episode is read-only. A correction opens a new episode rather than
                        changing what was signed.
                    </div>
                </div>

                {state.sign_off_note && (
                    <div className="cf-section">
                        <h4>Note at sign-off</h4>
                        <p>{state.sign_off_note}</p>
                    </div>
                )}

                {/* Answered here because this is where the question is asked: the
                    therapist has just signed the episode off and wants to know
                    whether it counts. No pseudonym — their question is whether
                    the child is in the dataset, not what their code is, and
                    showing the code beside a name would rebuild the link that
                    pseudonymising exists to break. */}
                {state.research && (
                    <div className={`cf-note ${state.research.in_dataset ? 'blue' : 'amber'}`}>
                        <b>
                            {state.research.in_dataset
                                ? 'In the research dataset.'
                                : 'Not in the research dataset.'}
                        </b>{' '}
                        {state.research.in_dataset
                            ? 'This episode is signed off and the family\u2019s research permission is live.'
                            : state.research.reason}
                    </div>
                )}

                <div className="cf-section">
                    <h4>The frozen record</h4>
                    <p className="cf-hint">
                        What the record said at that moment, kept apart from the live tables. The
                        live score sheet can still change — a word corrected in a later episode, a
                        measurement recomputed — and this copy will not.
                    </p>
                    {snapshot ? (
                        <>
                            <div className="cf-grid">
                                <div>
                                    <span className="cf-hint">Frozen</span>
                                    <div>{when(snapshot.taken_at)} by {snapshot.taken_by_name}</div>
                                </div>
                                <div>
                                    <span className="cf-hint">Episode</span>
                                    <div>{snapshot.episode_number}</div>
                                </div>
                                <div>
                                    <span className="cf-hint">Snapshot shape</span>
                                    <div>{snapshot.snapshot_version}</div>
                                </div>
                            </div>

                            <h4>Wording in force at the time</h4>
                            <table className="cf-measures">
                                <thead>
                                    <tr><th>Content</th><th>Version</th><th>Fingerprint</th></tr>
                                </thead>
                                <tbody>
                                    {Object.entries(snapshot.content_versions || {}).map(([name, v]) => (
                                        <tr key={name}>
                                            <td>
                                                {name}
                                                {v.provisional && (
                                                    <span className="cf-hint"> · provisional at the time</span>
                                                )}
                                            </td>
                                            <td>{v.version}</td>
                                            <td className="cf-hint">{(v.sha256 || '').slice(0, 12)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="cf-hint">
                                The fingerprint is there so a reader years from now can tell what
                                a word like “moderate” meant when it was chosen — the wording lives
                                in files that are expected to be revised.
                            </p>

                            {snapshot.impression && (
                                <>
                                    <h4>The impression that was signed</h4>
                                    <div className="cf-grid">
                                        <div>
                                            <span className="cf-hint">Primary</span>
                                            <div>{snapshot.impression.hypothesis || '—'}</div>
                                        </div>
                                        <div>
                                            <span className="cf-hint">Contributing features</span>
                                            <div>
                                                {(snapshot.impression.secondary_hypotheses || []).join(', ') || '—'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="cf-hint">Severity</span>
                                            <div>{snapshot.impression.severity || '—'}</div>
                                        </div>
                                    </div>
                                    {snapshot.impression.rationale && (
                                        <p>{snapshot.impression.rationale}</p>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <button type="button" className="btn btn-secondary" disabled={busy} onClick={showSnapshot}>
                            Open the frozen record
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ── not yet signed off ────────────────────────────────────────────────
    return (
        <div>
            {message && <div className="cf-note green">{message}</div>}
            {error && <div className="cf-note red">{error}</div>}

            <div className="cf-note amber">
                <b>Signing off cannot be undone.</b> The episode becomes read-only, and what the
                record says at that moment is frozen. A later correction opens a new episode.
            </div>

            <div className="cf-section">
                <h4>Before signing</h4>
                <ul className="cf-checklist">
                    {state.requirements.map((req) => (
                        <li key={req.key} className={req.satisfied ? 'done' : 'blocked'}>
                            <span className="cf-tick">{req.satisfied ? '✓' : '○'}</span>
                            <div>
                                {req.label}
                                {!req.satisfied && req.reason && (
                                    <div className="cf-why">{req.reason}</div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {state.advisories.length > 0 && (
                <div className="cf-section">
                    <h4>Worth knowing first</h4>
                    {state.advisories.map((item) => (
                        <div key={item.key} className="cf-note amber">{item.text}</div>
                    ))}
                    <p className="cf-hint">
                        None of these stops you signing. They are here because they become
                        permanent once the record is frozen.
                    </p>
                </div>
            )}

            <div className="cf-section">
                <label className="cf-field">
                    <span>Note at sign-off (optional)</span>
                    <textarea
                        rows="3" value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Anything a reader of this episode should know. The clinical reasoning belongs in the impression."
                    />
                </label>

                {!state.may_sign ? (
                    <div className="cf-note red">{state.may_sign_reason}</div>
                ) : !state.ready ? (
                    <p className="cf-hint">
                        {state.blocked_by.length} requirement(s) still to go. The list above says
                        what each one needs.
                    </p>
                ) : confirming ? (
                    <div className="cf-note red">
                        <b>Sign off episode {state.episode_number} for {state.child_name}?</b>
                        <div className="cf-hint">
                            This cannot be undone. The record is frozen as it reads now.
                        </div>
                        <div className="cf-actions">
                            <button type="button" className="btn btn-primary" disabled={busy} onClick={sign}>
                                {busy ? 'Signing…' : 'Yes, sign off'}
                            </button>
                            <button type="button" className="btn btn-secondary" disabled={busy}
                                onClick={() => setConfirming(false)}>
                                Not yet
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="cf-actions">
                        <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}>
                            Sign off this episode…
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
